import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import React, { useEffect, useRef } from "react";
import { ServicePage, ServiceRedirect, BlogPostPage, FAQPage, StatePage, SampleBlogPost } from "@/pages";
import { Homepage, USAHubPage, USAServicePage, USAStatePage, USAStateEntityPage, USAFilingPage, TermsPage, PrivacyPage, ContactPage, BlogIndexPage, BlogCategoryPage, LinkedinAIPage, LinkedinPrivacyPage, LinkedinContactPage, InvestmentDraftingPage, AiDocumentServicesPage, LawWatchIndexPage, LawWatchArticlePage } from "@/pages/public";
import { LoginPage, OTPVerifyPage, DashboardPage, CompaniesListPage, CompanyDetailPage, OrdersListPage, NewOrderPage, OrderDetailPage, DocumentsPage, GetStartedPage, FormationPackagePage, GoogleCallbackPage, SelfRegisterPage, AddCompanyPage, PaymentSuccessPage, DocVaultPage, DataRoomLanderPage, InternationalOnboardingPage, InternationalOrderPage } from "@/pages/app";
import VdrPortal from "@/pages/vdr/VdrPortal";
import DataRoomPage from "@/pages/DataRoomPage";
import UsaCountryPage from "@/pages/public/UsaCountryPage";
import USAManagedPayrollPage from "@/pages/public/USAManagedPayrollPage";
import USASalesTaxPage from "@/pages/public/USASalesTaxPage";
import USABookkeepingPage from "@/pages/public/USABookkeepingPage";
import UsaGuidePage from "@/pages/public/UsaGuidePage";
import MarketGuidePage from "@/pages/public/MarketGuidePage";
import { MARKET_GUIDE_SLUGS } from "@/components/public/marketGuides";
const MARKET_GUIDE_PATHS = Object.keys(MARKET_GUIDE_SLUGS);
import { 
  SuperAdminOverview, 
  SuperAdminOrders, 
  SuperAdminOrderDetail, 
  SuperAdminPayments, 
  SuperAdminProducts,
  SuperAdminStateFees,
  SuperAdminStateAddresses,
  SuperAdminStateFilings,
  SuperAdminUsers, 
  SuperAdminScheduler, 
  SuperAdminAudit, 
  SuperAdminSettings,
  SuperAdminCompanies,
  SuperAdminCompanyDetail,
  SuperAdminFAQ,
  SuperAdminBlog,
  SuperAdminLawWatch
} from "@/pages/superadmin";
import { AuthProvider } from "@/context/AuthContext";
import { GuideRequestProvider } from "@/components/public";
import ProtectedRoute from "@/components/ProtectedRoute";
import SuperAdminRoute from "@/components/superadmin/SuperAdminRoute";

// Legal IQ Operations Platform (isolated section - separate DB legal_iq_emergent)
import { LiqAuthProvider } from "./liq/auth";
import LiqLayout from "./liq/Layout";
import LiqLogin from "./liq/pages/Login";
import LiqMatters from "./liq/pages/Matters";
import LiqMatterDetail from "./liq/pages/MatterDetail";
import LiqClientHub from "./liq/pages/ClientHub";
import LiqClientDetail from "./liq/pages/ClientDetail";
import LiqAgentHub from "./liq/pages/AgentHub";

// Scroll to top on route change - with debounce to prevent infinite loops
function ScrollToTop() {
  const { pathname } = useLocation();
  const lastPathRef = useRef(pathname);
  
  useEffect(() => {
    // Only scroll if pathname actually changed (prevents re-render loops)
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname;
      try {
        window.scrollTo(0, 0);
      } catch (e) {
        // Silently ignore scroll errors
      }
    }
  }, [pathname]);
  
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <GuideRequestProvider>
          <ScrollToTop />
          <Routes>
            {/* ============================================ */}
            {/* APP ROUTES - starteaseai.com */}
            {/* ============================================ */}
            
            {/* Auth Pages */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-otp" element={<OTPVerifyPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
            
            {/* Protected Pages */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/companies" element={
              <ProtectedRoute>
                <CompaniesListPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/companies/self-register" element={
              <ProtectedRoute>
                <SelfRegisterPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/companies/new" element={
              <ProtectedRoute>
                <AddCompanyPage />
              </ProtectedRoute>
            } />
            <Route path="/payment/success" element={
              <ProtectedRoute>
                <PaymentSuccessPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/docvault" element={
              <ProtectedRoute>
                <DocVaultPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/data-room" element={
              <ProtectedRoute>
                <DataRoomLanderPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/companies/:companyId" element={
              <ProtectedRoute>
                <CompanyDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/companies/:companyId/get-started" element={
              <ProtectedRoute>
                <GetStartedPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/companies/:companyId/formation-package" element={
              <ProtectedRoute>
                <FormationPackagePage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/orders" element={
              <ProtectedRoute>
                <OrdersListPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/orders/new" element={
              <ProtectedRoute>
                <NewOrderPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/orders/international" element={
              <ProtectedRoute>
                <InternationalOrderPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/onboarding/international" element={
              <ProtectedRoute>
                <InternationalOnboardingPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/orders/:orderId" element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/documents" element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            } />
            
            {/* ============================================ */}
            {/* SUPER ADMIN ROUTES - /superadmin/* */}
            {/* ============================================ */}
            
            {/* Redirect /admin to /superadmin for backward compatibility */}
            <Route path="/admin" element={<Navigate to="/superadmin" replace />} />
            <Route path="/admin/*" element={<Navigate to="/superadmin" replace />} />

            <Route path="/superadmin" element={
              <SuperAdminRoute>
                <SuperAdminOverview />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/orders" element={
              <SuperAdminRoute>
                <SuperAdminOrders />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/orders/:orderId" element={
              <SuperAdminRoute>
                <SuperAdminOrderDetail />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/companies" element={
              <SuperAdminRoute>
                <SuperAdminCompanies />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/companies/:companyId" element={
              <SuperAdminRoute>
                <SuperAdminCompanyDetail />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/payments" element={
              <SuperAdminRoute>
                <SuperAdminPayments />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/products" element={
              <SuperAdminRoute>
                <SuperAdminProducts />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/products/state-fees" element={
              <SuperAdminRoute>
                <SuperAdminStateFees />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/products/state-addresses" element={
              <SuperAdminRoute>
                <SuperAdminStateAddresses />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/products/state-filings" element={
              <SuperAdminRoute>
                <SuperAdminStateFilings />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/users" element={
              <SuperAdminRoute>
                <SuperAdminUsers />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/scheduler" element={
              <SuperAdminRoute>
                <SuperAdminScheduler />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/audit" element={
              <SuperAdminRoute>
                <SuperAdminAudit />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/settings" element={
              <SuperAdminRoute>
                <SuperAdminSettings />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/faq" element={
              <SuperAdminRoute>
                <SuperAdminFAQ />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/blog" element={
              <SuperAdminRoute>
                <SuperAdminBlog />
              </SuperAdminRoute>
            } />
            <Route path="/superadmin/law-watch" element={
              <SuperAdminRoute>
                <SuperAdminLawWatch />
              </SuperAdminRoute>
            } />
            
            {/* ============================================ */}
            {/* PUBLIC WEBSITE - starteaseai.com */}
            {/* ============================================ */}
            
            {/* Homepage */}
            <Route path="/" element={<Homepage />} />
          
          {/* Terms and Privacy */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* LinkedIn AI Chrome Extension */}
          <Route path="/linkedin-ai" element={<LinkedinAIPage />} />
          <Route path="/linkedin-ai/" element={<LinkedinAIPage />} />
          <Route path="/linkedin-ai/privacy" element={<LinkedinPrivacyPage />} />
          <Route path="/linkedin-ai/contact" element={<LinkedinContactPage />} />

          {/* External Party Data Room Portal (OTP-gated, scoped JWT) */}
          <Route path="/vdr" element={<VdrPortal />} />
          <Route path="/vdr/" element={<VdrPortal />} />
          <Route path="/vdr/login" element={<VdrPortal />} />

          {/* Data Room marketing landing page */}
          <Route path="/data-room" element={<DataRoomPage />} />
          <Route path="/data-room/" element={<DataRoomPage />} />

          {/* Investment Drafting marketing landing page */}
          <Route path="/investment-drafting" element={<InvestmentDraftingPage />} />
          <Route path="/investment-drafting/" element={<InvestmentDraftingPage />} />
          <Route path="/ai-document-services" element={<AiDocumentServicesPage />} />
          <Route path="/ai-document-services/" element={<AiDocumentServicesPage />} />
          <Route path="/ai-law-watch" element={<LawWatchIndexPage />} />
          <Route path="/ai-law-watch/" element={<LawWatchIndexPage />} />
          <Route path="/ai-law-watch/:slug" element={<LawWatchArticlePage />} />
          
          {/* USA Hub */}
          <Route path="/usa" element={<UsaCountryPage />} />
          <Route path="/usa/" element={<UsaCountryPage />} />
          
          {/* USA States/Filings index redirects */}
          <Route path="/usa/states" element={<Navigate to="/usa/" replace />} />
          <Route path="/usa/states/" element={<Navigate to="/usa/" replace />} />
          <Route path="/usa/filings" element={<Navigate to="/usa/" replace />} />
          <Route path="/usa/filings/" element={<Navigate to="/usa/" replace />} />
          
          {/* USA Filing Pages (must come before generic /usa/:slug) */}
          <Route path="/usa/filings/:filing" element={<USAFilingPage />} />
          <Route path="/usa/filings/:filing/" element={<USAFilingPage />} />
          
          {/* USA State + Entity Detail Pages (must come before state overview) */}
          <Route path="/usa/states/:state/:entity" element={<USAStateEntityPage />} />
          <Route path="/usa/states/:state/:entity/" element={<USAStateEntityPage />} />
          
          {/* USA State Overview Pages */}
          <Route path="/usa/states/:state" element={<USAStatePage />} />
          <Route path="/usa/states/:state/" element={<USAStatePage />} />
          
          {/* USA Managed Payroll (must come before generic /usa/:slug) */}
          <Route path="/usa/payroll" element={<USAManagedPayrollPage />} />
          <Route path="/usa/payroll/" element={<USAManagedPayrollPage />} />

          {/* USA Managed Sales Tax (must come before generic /usa/:slug) */}
          <Route path="/usa/sales-tax" element={<USASalesTaxPage />} />
          <Route path="/usa/sales-tax/" element={<USASalesTaxPage />} />

          {/* USA Managed Bookkeeping (must come before generic /usa/:slug) */}
          <Route path="/usa/bookkeeping" element={<USABookkeepingPage />} />
          <Route path="/usa/bookkeeping/" element={<USABookkeepingPage />} />

          {/* USA Service Pages (Dynamic) - must come after /usa/states and /usa/filings */}
          <Route path="/usa/:slug" element={<USAServicePage />} />
          <Route path="/usa/:slug/" element={<USAServicePage />} />
          
          {/* FAQ Page (shared) */}
          <Route path="/faq" element={<FAQPage />} />
          
          {/* Blog Pages */}
          <Route path="/blog/us-company-formation-guide" element={<UsaGuidePage />} />
          {MARKET_GUIDE_PATHS.map((s) => (
            <Route key={s} path={`/blog/${s}`} element={<MarketGuidePage slug={s} />} />
          ))}
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/" element={<BlogIndexPage />} />
          <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
          <Route path="/blog/category/:category/" element={<BlogCategoryPage />} />
          <Route path="/blog/:slug" element={<MarketGuidePage />} />
          <Route path="/blog/:slug/" element={<MarketGuidePage />} />
          
          {/* ============================================ */}
          {/* INTERNAL/DEMO ROUTES (existing) */}
          {/* ============================================ */}
          
          {/* Sample Blog Post (Component Demo) */}
          <Route path="/sample-blog" element={<SampleBlogPost />} />
          
          {/* Service Pages — redirect to richer /usa/:slug/ marketing pages */}
          <Route path="/services/:slug" element={<ServiceRedirect />} />
          <Route path="/services/:slug/" element={<ServiceRedirect />} />
          
          {/* State Pages (old route - kept for compatibility) */}
          <Route path="/states/:slug" element={<StatePage />} />

          {/* Legal IQ Operations Platform (isolated - legal_iq_emergent DB) */}
          <Route path="/liq/login" element={<LiqAuthProvider><LiqLogin /></LiqAuthProvider>} />
          <Route path="/liq" element={<LiqAuthProvider><LiqLayout /></LiqAuthProvider>}>
            <Route index element={<Navigate to="/liq/agents" replace />} />
            <Route path="matters" element={<LiqMatters />} />
            <Route path="matters/:id" element={<LiqMatterDetail />} />
            <Route path="clients" element={<LiqClientHub />} />
            <Route path="clients/:id" element={<LiqClientDetail />} />
            <Route path="agents" element={<LiqAgentHub />} />
            {/* Legacy redirects */}
            <Route path="chats" element={<Navigate to="/liq/agents" replace />} />
            <Route path="employees" element={<Navigate to="/liq/agents" replace />} />
            <Route path="entities" element={<Navigate to="/liq/clients" replace />} />
            <Route path="tasks" element={<Navigate to="/liq/matters" replace />} />
            <Route path="approvals" element={<Navigate to="/liq/matters" replace />} />
            <Route path="jurisdictions" element={<Navigate to="/liq/matters" replace />} />
            <Route path="services" element={<Navigate to="/liq/matters" replace />} />
          </Route>

          {/* Catch-all: redirect unknown/removed paths to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </GuideRequestProvider>
      </BrowserRouter>
    </AuthProvider>
    </div>
  );
}

export default App;
